import { useCallback, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { showSessionExpiryToast } from "../utils/showSessionExpiryToast";
import { showError } from "../utils/error-handling";
import { useRefreshToken } from "../data/useRefreshToken";
import { env } from "../utils/env";
import { DateTime } from "luxon";
import { useCookies } from "react-cookie";
import { AUTH_COOKIE_NAME, useAuth } from "../context/AuthContext";
import { getJwtExpiryMs, getTokenExpiresAt } from "../utils/tokenExpiry";

const TOAST_ID = "session-expiry";
const WARNING_WINDOW_MS = 60000;

export const SessionManager = () => {
  const { credentials, logout, setCredentials } = useAuth();
  const [, setCookie] = useCookies();
  const isRefreshingRef = useRef(false);

  const { mutate: refresh } = useRefreshToken();

  // Trust the token's own `exp` claim; the API's `expires_in` is not always
  // in sync with the configured token lifetime.
  const expiresAt =
    getJwtExpiryMs(credentials?.accessToken) ?? credentials?.expiresAt;

  const handleLogout = useCallback(() => {
    toast.dismiss(TOAST_ID);
    logout();
  }, [logout]);

  const handleStayLoggedIn = useCallback(() => {
    // Block the interval from re-showing the toast while the refresh is in
    // flight (it still sees the old expiry until new credentials land).
    isRefreshingRef.current = true;
    toast.dismiss(TOAST_ID);

    refresh(
      { refresh: credentials?.refreshToken },
      {
        onSuccess: (res) => {
          const newCreds = {
            ...credentials,
            accessToken: res.access,
            refreshToken: credentials?.refreshToken,
            expires_in: res.expires_in,
            expiresAt: getTokenExpiresAt(res.access, res.expires_in),
          };
          setCredentials(newCreds);

          setCookie(AUTH_COOKIE_NAME, newCreds, {
            expires: DateTime.now()
              .plus({ milliseconds: env.cookieExpiryMs })
              .toJSDate(),
            path: "/",
            secure: true,
            sameSite: "strict",
          });
        },
        onError: (err: any) => {
          showError(err, "Problem with refresh token, please login again.");
          logout();
        },
        onSettled: () => {
          isRefreshingRef.current = false;
        },
      }
    );
  }, [credentials, logout, refresh, setCookie, setCredentials]);

  // Keep latest handlers in refs so the timer isn't torn down and rebuilt on
  // every AuthProvider render (logout is not memoised upstream).
  const stayLoggedInRef = useRef(handleStayLoggedIn);
  const logoutRef = useRef(handleLogout);
  stayLoggedInRef.current = handleStayLoggedIn;
  logoutRef.current = handleLogout;

  useEffect(() => {
    if (!expiresAt) return;

    const interval = setInterval(() => {
      const timeLeft = expiresAt - Date.now();

      if (timeLeft <= 0) {
        clearInterval(interval);
        logoutRef.current();
        return;
      }

      if (
        timeLeft <= WARNING_WINDOW_MS &&
        !isRefreshingRef.current &&
        !toast.isActive(TOAST_ID)
      ) {
        showSessionExpiryToast(
          Math.floor(timeLeft / 1000),
          () => stayLoggedInRef.current(),
          () => logoutRef.current()
        );
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [expiresAt]);

  return null;
};
